import { Box, Divider, Unstable_Grid2 as Grid, Stack } from '@mui/material';
import { useField, useForm } from 'react-final-form';
import { useCallback, useMemo } from 'react';

import { ToggleButtonGroupField } from '@occc-app/design-system';

import { LocationGroupMultipleSelect } from '@occc/feat-constraints';
import { respondedOptions } from './constants';
import { SurveyAnswersSelect } from './SurveyAnswersSelect';
import { SurveysSelect } from './SurveysSelect';
import { SurveySliderFormControls } from './SurveySliderFormControls';
import {
  getIsSingleOrMultipleChoiceQuestionResponded,
  getIsSliderQuestionResponded,
  getSurveyQuestion,
  getSurveysFieldName,
} from './utils';
import { MEMBER_RESPONDED_WITH_ANY_OF_LABEL } from './copies';
import { FieldTitle } from './FieldTitle';

import { SurveysFormValues } from '../../types';
import { DeleteIcon } from '../WalletSteering/components/DeleteIcon';
import { LocationMultipleSelectField } from '../../../Location/components/LocationSelect/LocationMultipleSelectField';

type SurveysFormArrayItemProps = {
  fieldNamePrefix: string;
  index: number;
  isDeleteIconShown: boolean;
  onDeleteClick(index: number): void;
};

export const SurveysFormArrayItem = ({
  index,
  fieldNamePrefix,
  isDeleteIconShown,
  onDeleteClick,
}: SurveysFormArrayItemProps) => {
  const form = useForm();
  const surveyAnswersFieldName = getSurveysFieldName(fieldNamePrefix, 'surveyAnswers');
  const {
    input: { value: values },
  } = useField<SurveysFormValues>(fieldNamePrefix, { subscription: { value: true } });

  const handleDeleteClick = useCallback(() => onDeleteClick(index), [index, onDeleteClick]);

  const clearSurveyAnswersOnSurveyChange = useCallback(() => {
    form.change(surveyAnswersFieldName, []);
  }, [form, surveyAnswersFieldName]);

  const renderSliderControls = () => {
    if (!values) {
      return;
    }

    if (!getIsSliderQuestionResponded(values)) {
      return;
    }

    return (
      <SurveySliderFormControls
        fieldNamePrefix={fieldNamePrefix}
        sliderOperator={values.sliderOperator}
      />
    );
  };

  const renderSingleOrMultipleChoiceControls = () => {
    if (!values) {
      return;
    }

    if (!getIsSingleOrMultipleChoiceQuestionResponded(values)) {
      return;
    }

    const surveyQuestion = getSurveyQuestion(values.survey);

    return (
      <Stack spacing={2}>
        <FieldTitle label={MEMBER_RESPONDED_WITH_ANY_OF_LABEL} />

        <SurveyAnswersSelect
          name={surveyAnswersFieldName}
          options={surveyQuestion.options}
          selectedUuids={values.surveyAnswers || []}
        />
      </Stack>
    );
  };

  const isNotRespondedTo = values?.respondedStatus === 'not-responded';

  const surveyFilterOverrides = useMemo(
    () => ({ includeBasedOnAggregatedAudience: isNotRespondedTo ? false : undefined }),
    [isNotRespondedTo],
  );

  return (
    <>
      {index > 0 && <Divider sx={{ mt: 2.5, mb: 3 }} />}

      <Grid container columnSpacing={3} rowSpacing={2} position='relative'>
        {isDeleteIconShown && (
          <Box position='absolute' top={0} right={0}>
            <DeleteIcon onClick={handleDeleteClick} />
          </Box>
        )}

        <Grid xs={12} sm='auto'>
          <Stack spacing={2}>
            <FieldTitle label='Member has' />

            <ToggleButtonGroupField
              name={getSurveysFieldName(fieldNamePrefix, 'respondedStatus')}
              buttons={respondedOptions}
            />
          </Stack>
        </Grid>

        <Grid xs={12} sm>
          <Stack spacing={2}>
            <FieldTitle
              label='Survey Name'
              infoText='Audiences cannot contain members that did not respond to a survey sent to a combined audience.'
              infoEnabled={isNotRespondedTo}
            />

            <SurveysSelect
              name={getSurveysFieldName(fieldNamePrefix, 'survey')}
              label='Select Survey'
              onChange={clearSurveyAnswersOnSurveyChange}
              surveyFilterOverrides={surveyFilterOverrides}
            />
          </Stack>
        </Grid>
      </Grid>

      {values?.respondedStatus === 'responded' && (
        <Grid container columnSpacing={3} rowSpacing={2} mt={2}>
          <Grid sm={6} xs={12}>
            <Stack spacing={2}>
              <FieldTitle label='At This Location' />

              <LocationMultipleSelectField
                inputProps={{
                  margin: 'none',
                }}
                name={getSurveysFieldName(fieldNamePrefix, 'locations')}
                label='Locations'
              />
            </Stack>
          </Grid>

          <Grid sm={6} xs={12}>
            <Stack spacing={2}>
              <FieldTitle label='At This Location Group' />

              <LocationGroupMultipleSelect
                inputProps={{
                  margin: 'none',
                }}
                name={getSurveysFieldName(fieldNamePrefix, 'locationGroups')}
                label='Location Groups'
              />
            </Stack>
          </Grid>

          <Grid sm={6} xs={12}>
            {renderSingleOrMultipleChoiceControls()}

            {renderSliderControls()}
          </Grid>
        </Grid>
      )}
    </>
  );
};
